import { IDS, KESTREL, LEVELS, ONTOLOGY, P, propertyById } from './kestrelOntology';
import { snowflakeAt } from './time';

/*
 * The EVP-750 passports: one pack unit (serial EVP750-2609-0147) with its
 * model and batch above it and its cells below, as the internal passport
 * screens and the public viewer receive them. Every builder takes a clock
 * (fixtures/time.js createClock) so "just now" stays true on the day a
 * visitor looks.
 *
 * The pack's values are the one set every fixture that shows it reads
 * (client R-2): the Home hero and bento, the Battery passport hero, the
 * Platform story and its customer phone, the genealogy, the brand swap and
 * /passport-example (PassportExample/examplePassport.js writes them out in
 * English, German and French; kestrelValues.test.js holds the two equal).
 * Its two dates are calendar days, not times ago: a date of manufacture
 * printed on a passport does not move with the day it is read.
 */

const PACK_SERIAL = 'EVP750-2609-0147';

/** The pack's date of manufacture, the day /passport-example prints too. */
export const PACK_MANUFACTURED = '2026-09-12';

/** When its batch's production started: two days before the pack was made. */
export const BATCH_PRODUCTION_STARTED = '2026-09-10';

/**
 * When the batch's import created the pack's passport and its cells': the
 * afternoon of its date of manufacture, a fixed instant like the date itself
 * (the summary rail prints it as Created beside the date of manufacture).
 */
export const PACK_CREATED_AT = '2026-09-12T14:20:00Z';

/** Stored values, `{ [propertyId]: { value } }`, as a passport's `data`. */
const stored = (values) => Object.fromEntries(Object.entries(values).map(([key, value]) => [P[key], { value }]));

const MODEL_FRIENDLY_ID = KESTREL.model;
const BATCH_FRIENDLY_ID = 'GOT-L2-2609-A';

/** The model's values (EVP-750, its warranty and mass shares). */
const modelValues = () => ({
    modelName: KESTREL.model,
    batteryCategory: 'Electric vehicle battery',
    cellCount: 768,
    warranty: '8 years or 160,000 km',
    massCells: 71.4,
    massHousing: 16.2,
    massThermal: 8.1,
    massElectronics: 4.3,
});

const batchValues = () => ({
    batchCode: BATCH_FRIENDLY_ID,
    productionLine: 'Göteborg L2',
    producedOn: BATCH_PRODUCTION_STARTED,
    plannedQuantity: 48,
    firstPassYield: 97.9,
});

/** The pack's safety instructions, as written in English and German. */
export const SAFETY_INSTRUCTIONS = Object.freeze({
    en: 'High voltage. Do not open the battery.',
    de: 'Hochspannung. Batterie nicht öffnen.',
});

/** The pack unit's values once complete, in ontology order. None is a time ago, so it takes no clock. */
const packValues = () => ({
    serial: PACK_SERIAL,
    manufacturer: KESTREL.name,
    plant: 'Göteborg Gigafactory, SE',
    manufactured: PACK_MANUFACTURED,
    chemistry: 'NMC811',
    cellFormat: 'Cylindrical 4680',
    ratedCapacity: 75.2,
    nominalVoltage: 346,
    cycleLife: 1500,
    carbonFootprint: 61.3,
    recycledCobalt: 18.4,
    recycledLithium: 7.2,
    recycledNickel: 9.1,
    stateOfHealth: 99.7,
    safetyInstructions: { ...SAFETY_INSTRUCTIONS },
});

/**
 * The values the hero sees arrive, in arrival order. Missing at the start (so
 * the passport opens at 10 of 15 set, 67%) and filled one by one.
 */
export const PENDING_KEYS = ['carbonFootprint', 'recycledCobalt', 'recycledLithium', 'recycledNickel', 'stateOfHealth'];

/** The 12 cells the pack's first page shows (of the 768 the model declares). */
const CELL_TABLE = [
    ['K46-2608-31421', 'M01-P01', 3.614, 27.31, 1.91],
    ['K46-2608-31422', 'M01-P02', 3.615, 27.28, 1.88],
    ['K46-2608-31423', 'M01-P03', 3.613, 27.35, 1.93],
    ['K46-2608-31424', 'M01-P04', 3.616, 27.22, 1.87],
    ['K46-2608-31425', 'M01-P05', 3.614, 27.30, 1.90],
    ['K46-2608-31426', 'M01-P06', 3.612, 27.36, 1.95],
    ['K46-2608-31427', 'M01-P07', 3.615, 27.27, 1.89],
    ['K46-2608-31428', 'M01-P08', 3.617, 27.19, 1.86],
    ['K46-2608-31429', 'M02-P01', 3.613, 27.33, 1.92],
    ['K46-2608-31430', 'M02-P02', 3.614, 27.29, 1.90],
    ['K46-2608-31431', 'M02-P03', 3.616, 27.24, 1.88],
    ['K46-2608-31432', 'M02-P04', 3.615, 27.26, 1.89],
];

export const CELL_IDS = CELL_TABLE.map((_, index) => snowflakeAt('pass', '2026-09-08T10:12:31Z', index + 1));

const cellValues = ([serial, position, voltage, capacity, impedance]) => ({
    cellSerial: serial,
    modulePosition: position,
    cellVoltage: voltage,
    cellCapacity: capacity,
    cellImpedance: impedance,
});

const change = (sequence, property, oldValue, newValue, at, extra = {}) => ({
    change_id: snowflakeAt('chg', '2026-09-08T10:12:30Z', sequence),
    property,
    old_value: oldValue,
    new_value: newValue,
    updated_at: at.naive,
    ...extra,
});

/**
 * The pack's history before the hero's values arrive, oldest first (the order
 * the passport read embeds it in): the import that created it on its day of
 * manufacture (PACK_CREATED_AT), its safety instructions three days ago, a
 * corrected voltage two days ago, and the cycle life set 12 minutes ago.
 */
function baseChangelog(clock) {
    const values = packValues();
    const imported = clock.at(PACK_CREATED_AT);
    const importedKeys = ['serial', 'manufacturer', 'plant', 'manufactured', 'chemistry', 'cellFormat', 'ratedCapacity'];

    return [
        change(1, 'publish_status', null, 'draft', imported),
        ...importedKeys.map((key, index) => change(index + 2, P[key], null, values[key], imported)),
        change(9, P.nominalVoltage, null, 345.6, imported),
        change(12, P.safetyInstructions, null, values.safetyInstructions, clock.ago({ days: 3 })),
        change(10, P.nominalVoltage, 345.6, values.nominalVoltage, clock.ago({ days: 2, hours: 4 })),
        change(11, P.cycleLife, null, values.cycleLife, clock.ago({ minutes: 12 })),
    ];
}

/** When each pending value arrives: a few seconds apart, all "just now". */
const pendingAt = (clock, index) => clock.ago({ seconds: 50 - index * 8 });

/**
 * The pack unit's `passportData` (PassportEdit's shape) at a point in the
 * hero's story:
 *
 * - `resolved`: how many of PENDING_KEYS have arrived (0 to 5), each with its
 *   changelog entry, so the Changelog count and "Updated" move with them.
 * - `released`: whether the pack has been released as #1 (a status entry, the
 *   `released` status, the release lock).
 *
 * The defaults are the end of the story: complete and released.
 */
export function packPassport(clock, { resolved = PENDING_KEYS.length, released = true } = {}) {
    const values = packValues();
    const arrived = PENDING_KEYS.slice(0, Math.max(0, Math.min(resolved, PENDING_KEYS.length)));
    const missing = new Set(PENDING_KEYS.filter((key) => !arrived.includes(key)));
    const present = Object.fromEntries(Object.entries(values).filter(([key]) => !missing.has(key)));

    const changelog = [
        ...baseChangelog(clock),
        ...arrived.map((key, index) => change(20 + index, P[key], null, values[key], pendingAt(clock, index))),
        ...(released ? [change(30, 'publish_status', 'draft', 'released', clock.ago({ seconds: 4 }), { release_number: 1 })] : []),
    ];
    const created = clock.at(PACK_CREATED_AT);

    return {
        snowflake: IDS.pack,
        friendly_id: PACK_SERIAL,
        tppmp: `${LEVELS.pack}:${IDS.batch}/${IDS.pack}`,
        parent_snowflake: IDS.batch,
        ancestors: [
            { snowflake: IDS.model, friendly_id: MODEL_FRIENDLY_ID, depth: LEVELS.model, depth_name: ONTOLOGY.data.A.name },
            { snowflake: IDS.batch, friendly_id: BATCH_FRIENDLY_ID, depth: LEVELS.batch, depth_name: ONTOLOGY.data.B.name },
        ],
        data: stored(present),
        changelog,
        changelog_cursor: null,
        publish_status: released ? 'released' : 'draft',
        release_number: released ? 1 : 0,
        meta: { created_at: created.naive, updated_at: changelog[changelog.length - 1].updated_at },
    };
}

/**
 * The pack's 12 cells as a child list row (RenderIterativeItem's shape).
 * `released`: how many of them, from the top, have been released with the
 * pack; the rest are drafts.
 */
export function cellRows(clock, { released = 0 } = {}) {
    const created = clock.at(PACK_CREATED_AT);
    const edited = clock.ago({ minutes: 12 });

    return CELL_TABLE.map((row, index) => {
        const isReleased = index < released;

        return {
            snowflake: CELL_IDS[index],
            friendly_id: row[0],
            publish_status: isReleased ? 'released' : 'draft',
            release_number: isReleased ? 1 : 0,
            meta: { created_at: created.naive, updated_at: isReleased ? clock.ago({ seconds: 4 }).naive : edited.naive },
        };
    });
}

/** Values the public endpoint serves: public properties only, as it strips the rest. */
const publicOnly = (data) => Object.fromEntries(
    Object.entries(data).filter(([id]) => propertyById(id)?.visibility?.public === true),
);

const ref = (key, depth, overrides = {}) => ({
    id: `ref_${key}`,
    property_id: P[key],
    node_id: depth,
    show_label: true,
    ...overrides,
});

/**
 * Kestrel's Composer layout for the pack's public page: the masthead, an
 * overview tab (identity, the battery, performance, recycled content as stat
 * tiles, the model's mass breakdown as a bar chart, the cells) and a
 * sustainability tab.
 */
export const COMPOSITION = {
    version: 2,
    masthead: {
        name: { source: 'property', property_id: P.modelName, node_id: LEVELS.model },
        // The product image: a Drive file reference, with the `link` the
        // public read hydrates onto it. Here the link is a site asset
        // (same origin, under public/): the pack's catalogue photo.
        image: {
            snowflake: IDS.packImage,
            filename: 'kestrel-evp750.webp',
            size: 26874,
            path_dir: null,
            link: '/images/landing/kestrel-evp750.webp',
        },
        fields: [
            { id: 'mf_serial', source: 'property', property_id: P.serial, node_id: LEVELS.pack, label: 'Serial number' },
            { id: 'mf_chemistry', source: 'property', property_id: P.chemistry, node_id: LEVELS.pack, label: 'Chemistry' },
            { id: 'mf_capacity', source: 'property', property_id: P.ratedCapacity, node_id: LEVELS.pack, label: 'Rated capacity' },
        ],
    },
    tabs: [
        {
            id: 'tab_overview',
            name: 'overview',
            label: 'Overview',
            icon: 'info',
            cards: [
                { id: 'card_identity', type: 'identity', title: 'Identity', size: 'half', sections: [] },
                {
                    id: 'card_battery',
                    type: 'standard',
                    title: 'Battery',
                    size: 'half',
                    sections: [{
                        id: 'sec_battery',
                        type: 'fields',
                        title: '',
                        properties: [
                            ref('manufacturer', LEVELS.pack),
                            ref('plant', LEVELS.pack),
                            ref('manufactured', LEVELS.pack),
                            ref('chemistry', LEVELS.pack),
                            ref('cellFormat', LEVELS.pack),
                        ],
                    }],
                },
                {
                    id: 'card_performance',
                    type: 'standard',
                    title: 'Performance',
                    size: 'full',
                    sections: [{
                        id: 'sec_performance',
                        type: 'grid',
                        title: '',
                        options: { columns: 3 },
                        properties: [ref('ratedCapacity', LEVELS.pack), ref('nominalVoltage', LEVELS.pack), ref('cycleLife', LEVELS.pack)],
                    }],
                },
                // Stat tiles, not a chart: composed while the pack unit level
                // was `many` (a series since 2026-10-05), when the viewer
                // plotted its refs once per embedded child of that level
                // (lib/compositionChart buildChartPoints), so a chart of the
                // pack's own shares drew nothing on the pack's own page.
                {
                    id: 'card_recycled',
                    type: 'standard',
                    title: 'Recycled content',
                    size: 'full',
                    sections: [{
                        id: 'sec_recycled',
                        type: 'grid',
                        title: 'Share of recycled material',
                        options: { columns: 3 },
                        properties: [ref('recycledCobalt', LEVELS.pack), ref('recycledLithium', LEVELS.pack), ref('recycledNickel', LEVELS.pack)],
                    }],
                },
                // The chart: the model's mass breakdown, four bars (the model
                // level is `singular`, so each ref is one point).
                {
                    id: 'card_mass',
                    type: 'standard',
                    title: 'Composition',
                    size: 'full',
                    sections: [{
                        id: 'sec_mass_chart',
                        type: 'chart',
                        title: 'Share of pack mass',
                        options: { chart_type: 'bar' },
                        properties: [
                            ref('massCells', LEVELS.model, { label_override: 'Cells' }),
                            ref('massHousing', LEVELS.model, { label_override: 'Housing' }),
                            ref('massThermal', LEVELS.model, { label_override: 'Thermal' }),
                            ref('massElectronics', LEVELS.model, { label_override: 'Electronics' }),
                        ],
                    }],
                },
                { id: 'card_cells', type: 'children', title: 'Cells', size: 'full', config: { child_node: LEVELS.cell }, sections: [] },
            ],
        },
        {
            id: 'tab_sustainability',
            name: 'sustainability',
            label: 'Sustainability',
            icon: 'leaf',
            cards: [{
                id: 'card_footprint',
                type: 'standard',
                title: 'Carbon footprint',
                size: 'full',
                sections: [{ id: 'sec_footprint', type: 'fields', title: '', properties: [ref('carbonFootprint', LEVELS.pack)] }],
            }],
        },
    ],
};

/**
 * Kestrel's Design page palette: the 11-step scale generated from the brand
 * orange, lightest first, `{ lightness, hex }` as the Design page stores it.
 * Step 500 is the accent.
 */
export const PALETTE = [
    [50, '#FFF6F0'], [100, '#FFEADD'], [200, '#FDD0B3'], [300, '#FAAE80'], [400, '#F6874A'],
    [500, '#F2641F'], [600, '#D94E10'], [700, '#B43D0D'], [800, '#8F3311'], [900, '#742D12'], [950, '#3F1407'],
].map(([lightness, hex]) => ({ lightness, hex }));

/**
 * The pack's public page payload, exactly as
 * PassportController::retrievePublicPassport returns it: the design and brand,
 * the passport keyed by level (the pack, its model and batch above, the first
 * page of its cells below), the ontology, the composition. Released, public
 * audience, internal values stripped.
 */
export function publicPayload(clock) {
    const pack = packPassport(clock);

    return {
        design: { palette: PALETTE, shadow: 1, rounding: 4, logo_url: null },
        brand: { name: KESTREL.name },
        passport: {
            [LEVELS.model]: { snowflake: IDS.model, friendly_id: MODEL_FRIENDLY_ID, data: publicOnly(stored(modelValues())) },
            [LEVELS.batch]: { snowflake: IDS.batch, friendly_id: BATCH_FRIENDLY_ID, data: publicOnly(stored(batchValues())) },
            [LEVELS.pack]: { snowflake: IDS.pack, friendly_id: PACK_SERIAL, data: publicOnly(pack.data) },
            [LEVELS.cell]: {
                parent_snowflake: IDS.pack,
                next_cursor: 'cursor-cells-2',
                children: CELL_TABLE.map((row, index) => ({
                    snowflake: CELL_IDS[index],
                    friendly_id: row[0],
                    data: publicOnly(stored(cellValues(row))),
                })),
            },
        },
        ontology: ONTOLOGY,
        composition: COMPOSITION,
        discoverable: [LEVELS.model, LEVELS.batch, LEVELS.pack, LEVELS.cell],
        publish_status: 'released',
        audience: 'public',
    };
}

/** The model and batch passports, for a vignette that shows them. */
export function ancestorPassports(clock) {
    return {
        model: { snowflake: IDS.model, friendly_id: MODEL_FRIENDLY_ID, data: stored(modelValues()) },
        batch: { snowflake: IDS.batch, friendly_id: BATCH_FRIENDLY_ID, data: stored(batchValues()) },
    };
}

export { PACK_SERIAL, BATCH_FRIENDLY_ID, MODEL_FRIENDLY_ID, modelValues, batchValues, packValues, cellValues, CELL_TABLE };
